import { Injectable } from "@angular/core";
import { AlertService, MessageSeverity } from "./alert.service";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";

@Injectable({
  providedIn: "root",
})
export class ErrorHandlerService {
  constructor(
    private alertService: AlertService,
    private router: Router,
  ) {}

  private lastIpBlockedAt = 0;
  private lastForbiddenAt = 0;

  handleError(error: HttpErrorResponse) {
    this.alertService.stopLoadingMessage();
    const message: string = error?.error?.message ?? "";
    // Login and 2FA code requests: the login form shows these errors inline; a global sticky toast
    // would also linger after a successful sign-in.
    const url = error?.url ?? "";
    if (/\/connect\/token/i.test(url) || /\/TwoFactor\/send/i.test(url)) {
      return;
    }
    if (/two factor required/i.test(message)) {
      return;
    }
    // IP allowlist: every API call on a page fails the same way; show one clear message and keep
    // the session (logging out would only bounce back through login from the same network).
    if (error.status === 403 && /ip address is not allowed/i.test(message)) {
      const now = Date.now();
      if (now - this.lastIpBlockedAt > 10000) {
        this.lastIpBlockedAt = now;
        this.alertService.showStickyMessage(
          "Access blocked",
          "Access from this IP address is not allowed. Connect from an approved network or ask an administrator to add your IP address to the allowlist.",
          MessageSeverity.error,
        );
      }
      return;
    }
    // Role not allowed (403 without a body): generic message, keep the session.
    if (error.status === 403 && !message && !(error.error instanceof Blob)) {
      const now = Date.now();
      if (now - this.lastForbiddenAt > 5000) {
        this.lastForbiddenAt = now;
        this.alertService.showMessage(
          "Access denied",
          "You don't have access to this",
          MessageSeverity.error,
        );
      }
      return;
    }
    const isblob = error.error instanceof Blob;
    if (isblob) {
      const blob = error.error as Blob;
      blob.text().then((p) => {
        this.alertService.showMessage(
          "Error Occurred",
          JSON.parse(p).message,
          MessageSeverity.error,
        );
      });
      return;
    }
    if (error.status >= 500 || error.status === 401) {
      if (!error.error || !error.error.exception) {
        this.alertService.showMessage(
          "Error Occurred",
          "An error occurred while performing operation",
          MessageSeverity.error,
        );
      } else {
        this.alertService.showMessage(
          "Error Occurred",
          error.error.exception,
          MessageSeverity.error,
        );
      }
    } else if (error.status === 0) {
      this.alertService.showMessage(
        "Error Occurred",
        "An error occurred while performing operation",
        MessageSeverity.error,
      );
    }
    if (error.error && error.error.message) {
      this.alertService.showStickyMessage(
        "Error",
        error.error.message,
        MessageSeverity.error,
      );
      if (error.error.errors && error.error.errors.length > 0) {
        error.error.errors.forEach((m: string) => {
          this.alertService.showStickyMessage(
            "Error",
            m,
            MessageSeverity.error,
          );
        });
      }
    } else if (
      error.error &&
      error.error.errors &&
      error.error.errors.length > 0
    ) {
      error.error.errors.forEach((m: string) => {
        this.alertService.showStickyMessage("Error", m, MessageSeverity.error);
      });
    } else if (
      error.error &&
      error.error.error &&
      error.error.error_description
    ) {
      console.log("Here");
      this.alertService.showStickyMessage(
        error.error.error,
        error.error.error_description,
        MessageSeverity.error,
      );
    }
  }

  handleSessionExpired() {
    this.alertService.stopLoadingMessage();
    this.alertService.showStickyMessage(
      "Error",
      "Your session has expired.\n\rPlease login again.\r\nThank you",
      MessageSeverity.error,
    );
    setTimeout(() => {
      this.router.navigateByUrl("/logout");
    }, 1000);
  }
}
